import { Link } from "@tanstack/react-router";
import { SITE } from "@/lib/site";
import { SponsorBlock } from "@/components/site/SponsorBlock";
import { PreferredSourceButton } from "@/components/site/PreferredSourceButton";


const COPYRIGHT_YEAR = 2026;

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border bg-paper-soft">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <h2 className="headline-display text-2xl">{SITE.name}</h2>
            <p className="mt-3 text-sm text-muted-foreground max-w-md">
              The authoritative newsroom on Google Maps, Google Business Profile, and local
              search. Read daily by local SEO agencies, GBP specialists, and the multi-location
              operators who need to know what changed this week.
            </p>
            <div className="mt-4 flex items-center gap-4">
              <a
                href="https://x.com/mapobserverseo"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Map Observer on X"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://mastodon.social/@mapobserver"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Map Observer on Mastodon"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M23.268 5.313c-.35-2.578-2.617-4.61-5.304-5.004C17.51.242 15.792 0 11.813 0h-.03c-3.98 0-4.835.242-5.288.309C3.882.692 1.496 2.518.917 5.127.64 6.412.61 7.837.661 9.143c.074 1.874.088 3.745.26 5.611.118 1.24.325 2.47.62 3.68.55 2.237 2.777 4.098 4.96 4.857 2.336.792 4.849.923 7.256.38.265-.061.527-.132.786-.213.585-.184 1.27-.39 1.774-.753a.057.057 0 0 0 .023-.043v-1.809a.052.052 0 0 0-.02-.041.053.053 0 0 0-.046-.01 20.282 20.282 0 0 1-4.709.545c-2.73 0-3.463-1.284-3.674-1.818a5.593 5.593 0 0 1-.319-1.433.053.053 0 0 1 .066-.054c1.517.363 3.072.546 4.632.546.376 0 .75 0 1.125-.01 1.57-.044 3.224-.124 4.768-.422.038-.008.077-.015.11-.024 2.435-.464 4.753-1.92 4.989-5.604.008-.145.03-1.52.03-1.67.002-.512.167-3.63-.024-5.545zm-3.748 9.195h-2.561V8.29c0-1.309-.55-1.976-1.67-1.976-1.23 0-1.846.79-1.846 2.35v3.403h-2.546V8.663c0-1.56-.617-2.35-1.848-2.35-1.112 0-1.668.668-1.67 1.977v6.218H4.822V8.102c0-1.31.337-2.35 1.011-3.12.696-.77 1.608-1.164 2.74-1.164 1.311 0 2.302.5 2.962 1.498l.638 1.06.638-1.06c.66-.999 1.65-1.498 2.96-1.498 1.13 0 2.043.395 2.74 1.164.675.77 1.012 1.81 1.012 3.12z" />
                </svg>
              </a>
              <a
                href="https://bsky.app/profile/mapobserver.bsky.social"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="Map Observer on Bluesky"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <p className="eyebrow">Sections</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/category/$slug" params={{ slug: "google-business-profile" }} className="hover:text-accent">Google Business Profile</Link></li>
              <li><Link to="/category/$slug" params={{ slug: "local-rankings" }} className="hover:text-accent">Local Rankings</Link></li>
              <li><Link to="/category/$slug" params={{ slug: "reviews-reputation" }} className="hover:text-accent">Reviews & Reputation</Link></li>
              <li><Link to="/category/$slug" params={{ slug: "google-maps" }} className="hover:text-accent">Google Maps</Link></li>
              <li><Link to="/category/$slug" params={{ slug: "ai-search" }} className="hover:text-accent">AI & Search</Link></li>
              <li><Link to="/category/$slug" params={{ slug: "industry-news" }} className="hover:text-accent">Industry News</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow">Trackers &amp; data</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/local-search-index" className="hover:text-accent">Local Search Index</Link></li>
              <li><Link to="/updates" className="hover:text-accent">Algorithm updates</Link></li>
              <li><Link to="/changelog" className="hover:text-accent">GBP &amp; Maps changelog</Link></li>
              <li><Link to="/state-of-local-search" className="hover:text-accent">State of Local Search</Link></li>
              <li><Link to="/glossary" className="hover:text-accent">Glossary</Link></li>
              <li><Link to="/guide" className="hover:text-accent">All guides</Link></li>
              <li><Link to="/tools" className="hover:text-accent">Reference tools</Link></li>
              <li><Link to="/embeds" className="hover:text-accent">Free embeds</Link></li>
              <li><Link to="/about" className="hover:text-accent">About</Link></li>
              <li><a href="/rss.xml" className="hover:text-accent">RSS feed</a></li>
              <li><a href="/sitemap.xml" className="hover:text-accent">Sitemap</a></li>
              <li><a href="/llms.txt" className="hover:text-accent">For AI crawlers</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 text-sm">
          <div>
            <p className="eyebrow">Newsroom</p>
            <ul className="mt-3 grid grid-cols-2 gap-y-2">
              <li><Link to="/newsroom" className="hover:text-accent">Masthead</Link></li>
              <li><Link to="/editorial-standards" className="hover:text-accent">Editorial standards</Link></li>
              <li><Link to="/corrections" className="hover:text-accent">Corrections</Link></li>
              <li><Link to="/ethics" className="hover:text-accent">Ethics</Link></li>
              <li><Link to="/ownership-and-funding" className="hover:text-accent">Ownership &amp; funding</Link></li>
              <li><Link to="/press" className="hover:text-accent">Press &amp; citation</Link></li>
              <li><Link to="/write-for-us" className="hover:text-accent">Write for us</Link></li>
              <li><Link to="/sponsors/gbply" className="hover:text-accent">Our sponsor</Link></li>
            </ul>

          </div>
          <div>
            <p className="eyebrow">Developers</p>
            <ul className="mt-3 grid grid-cols-2 gap-y-2">
              <li><Link to="/api/v1" className="hover:text-accent">Public API</Link></li>
              <li><a href="/api/public/v1/updates" className="hover:text-accent">Updates JSON</a></li>
              <li><a href="/api/public/v1/volatility" className="hover:text-accent">Volatility JSON</a></li>
              <li><a href="/api/public/v1/categories" className="hover:text-accent">Categories JSON</a></li>
              <li><a href="/llms-full.txt" className="hover:text-accent">Full-text feed</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 border-t border-border pt-6">
          <p className="eyebrow">Follow us in Google</p>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Choose Map Observer as a preferred source and Google will surface our local search
            reporting higher in your Top Stories.
          </p>
          <PreferredSourceButton placement="footer" variant="inline" className="mt-3" />
        </div>

        <SponsorBlock />

        <div className="mt-10 border-t border-border pt-6 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-3">
          <span>© {COPYRIGHT_YEAR} {SITE.name}. All rights reserved.</span>
          <span>For local SEO agencies, GBP specialists, and multi-location operators.</span>
        </div>
      </div>
    </footer>
  );
}

